import React, { FC } from "react";
import { useParams } from "react-router-dom";

import {
  toDateString,
  currentFormatTimestamp,
  formatDateTime,
  eachDayOfIntervals,
} from "~/libs";

import { arrayToCsv, downloadBlob } from "~/utils";

import {
  useProvidersCurrentEmployee,
  useDateRange,
  useMultiDivisionMultiSectionDropdown,
  useApisManagersEmployeeConditionSurveysProgressIndex,
} from "~/hooks";

import {
  Label,
  Button,
  RightSidebarTitle,
  LineChart,
} from "~/components/atoms";
import {
  EmployeeLayout,
  EmployeeLayoutMainContent,
  EmployeeLayoutRightSidebar,
} from "~/components/layouts";
import { TitleWithChild } from "~/components/molecules";
import {
  SelectDateRangeButtons,
  MultiParentsWithMultiChildrenDropdownField,
  FilterWrapper,
  NoDataDisplay,
  ProgressTableWithQuestions,
  RichHeader,
} from "~/components/organisms";
import { ProgressTableCellProps } from "~/components/organisms/ProgressTable/ProgressTableBase";

import {
  EmployeeConditionSurveysQuestionWithAnswers,
  TextAreaType,
} from "~/domains";

type RenderCellProps = ProgressTableCellProps & {
  question: EmployeeConditionSurveysQuestionWithAnswers;
};

export const ManagersEmployeeConditionSurveysProgressShow: FC = () => {
  const { employeeConditionSurveyId = "" } = useParams<{
    employeeConditionSurveyId: string;
  }>();
  const { currentEmployee } = useProvidersCurrentEmployee();

  const [{ startDate, endDate, onChange }] = useDateRange([null, null]);

  const {
    selectableDivisions,
    selectedDivisions,
    onDivisionChange,
    optionSelectableSections,
    optionSelectedSections,
    onSectionChange,
  } = useMultiDivisionMultiSectionDropdown({
    selectableDivisions: currentEmployee?.businessDivisions,
    selectableSections: currentEmployee?.businessSections,
  });

  const { data: progressData } =
    useApisManagersEmployeeConditionSurveysProgressIndex({
      employeeConditionSurveyId,
      params: {
        startDate: startDate ? toDateString(startDate) : "",
        endDate: endDate ? toDateString(endDate) : "",
        businessDivisionIds: selectedDivisions.map((division) => division.id),
        businessSectionIds: optionSelectedSections.map((section) => section.id),
      },
      config: {
        enabled:
          !!startDate &&
          !!endDate &&
          selectedDivisions.length > 0 &&
          optionSelectedSections.length > 0,
      },
    });

  // CSVエクスポートハンドラ
  const handleCSVExport = () => {
    if (!progressData) return;

    const csvData = generateCsvData();
    const fileName = `${currentFormatTimestamp}-コンディション進捗一覧.csv`;

    downloadBlob(arrayToCsv(csvData), fileName);
  };

  // パンくずリスト設定
  const breadCrumbItems = [
    {
      label: "コンディション",
      href: "/managers/employee_condition_surveys",
    },
    {
      label: "投稿一覧",
      href: `/managers/employee_condition_surveys/${employeeConditionSurveyId}/progress`,
    },
  ];

  // 日付関連のユーティリティ関数
  const getDateArray = (
    startDate: Date | null,
    endDate: Date | null,
  ): string[] => {
    if (!startDate || !endDate) return [];
    return eachDayOfIntervals({ start: startDate, end: endDate }).map(
      toDateString,
    );
  };

  const dates = getDateArray(startDate, endDate);

  const columns = dates.map((date) => ({
    date,
    isWeekend: [0, 6].includes(new Date(date).getDay()),
  }));

  // データの有無をチェック
  const hasData = progressData && progressData.questions.length > 0;
  const hasNoData =
    (!progressData || progressData.questions.length < 1) && dates.length < 1;

  // 平均ポイント推移チャートをレンダリング
  const renderAveragePointsChart = () => {
    if (!progressData) return null;

    const questionsWithPoints = progressData.questions.filter(
      (question) =>
        question.dailyAveragePoints && question.dailyAveragePoints.length > 0,
    );

    if (questionsWithPoints.length === 0) return null;

    const chartLines = questionsWithPoints.map((question, index) => ({
      name: `質問${index + 1}: ${question.question.length > 20 ? question.question.substring(0, 20) + "..." : question.question}`,
      data:
        question.dailyAveragePoints?.map((avg) => ({
          x: formatDateTime(avg.date, "MM/dd"),
          y: avg.averagePoint,
        })) || [],
    }));

    return (
      <div className="bg-white p-6 rounded-lg shadow">
        <h3 className="text-lg font-semibold text-primary-600 mb-4">
          質問別平均ポイント推移
        </h3>
        <LineChart
          title="日別平均ポイント"
          lines={chartLines}
          height={400}
          xAxis={{ title: "日付" }}
          yAxis={{ title: "平均ポイント", min: 0 }}
          className="w-full"
        />
      </div>
    );
  };

  // セルレンダリング関数
  const renderCell = ({ date, employee, question }: RenderCellProps) => {
    const employeeAnswer = question?.employeeAnswers.find(
      (ea) => ea.employee.id === employee.id,
    );
    const answer = employeeAnswer?.dailyAnswers.find(
      (answer) => answer.date === date,
    );
    const answerOption = question?.options.find(
      (option) => option.id === answer?.selectedOptionId,
    );

    // TextArea質問の場合は回答済みマークを表示
    if (question.questionTypeId === TextAreaType.id && answer?.answerText) {
      return (
        <div className="flex flex-col items-center space-y-1">
          <div className="text-xs font-medium text-primary-600 px-2 py-1 bg-primary-100 rounded-full border border-primary-300">
            回答済み
          </div>
        </div>
      );
    }

    // 回答がある場合
    if (answer) {
      return (
        <div className="flex flex-col items-center space-y-1">
          {answerOption?.fileUrl && (
            <div className="relative">
              <img
                src={answerOption.fileUrl}
                alt={answerOption.label}
                className="w-8 h-8 rounded-lg shadow-sm hover:scale-110 transition-transform duration-200"
              />
            </div>
          )}
          {answer.selectedOptionLabel && (
            <span className="text-xs font-medium text-secondary-600 px-2 py-1 bg-secondary-200 rounded-full">
              {answer.selectedOptionLabel}
            </span>
          )}
        </div>
      );
    }

    // 回答がない場合
    return (
      <div className="w-6 h-6 border-2 border-secondary-400 rounded-full opacity-30" />
    );
  };

  // CSVデータ生成関数
  const generateCsvData = (): string[][] => {
    if (!progressData) return [];

    // CSVヘッダーを生成
    const header: string[] = [
      "氏名",
      ...dates.map((date) => formatDateTime(date, "MM/dd")),
    ];

    // 各従業員の回答データを集約
    const employeeAnswersMap = new Map<
      string,
      {
        employeeName: string;
        dailyAnswers: Array<{
          date: string;
          selectedOptionLabel?: string;
        }>;
      }
    >();

    // 全ての質問から従業員の回答を統合
    progressData.questions.forEach((question) => {
      question.employeeAnswers.forEach((employeeAnswer) => {
        const employeeId = employeeAnswer.employee.id;

        if (!employeeAnswersMap.has(employeeId)) {
          employeeAnswersMap.set(employeeId, {
            employeeName: employeeAnswer.employee.name,
            dailyAnswers: [],
          });
        }

        const existingData = employeeAnswersMap.get(employeeId)!;
        existingData.dailyAnswers.push(...employeeAnswer.dailyAnswers);
      });
    });

    // CSV行データを生成
    const rows: string[][] = Array.from(employeeAnswersMap.values()).map(
      (employee) => [
        employee.employeeName,
        ...dates.map((date) => {
          const answer = employee.dailyAnswers.find(
            (answer) => answer.date === date,
          );
          return answer?.selectedOptionLabel || "";
        }),
      ],
    );

    return [header, ...rows];
  };

  return (
    <EmployeeLayout withRightSidebar breadCrumbItems={breadCrumbItems}>
      <EmployeeLayoutMainContent withRightSidebar>
        {/* フィルター部分 */}
        <FilterWrapper>
          <Label
            labelText="期間指定"
            className="text-sm font-semibold text-secondary-600"
            required={false}
            htmlFor="dateRange"
          />
          <SelectDateRangeButtons
            startDate={startDate}
            endDate={endDate}
            onChange={onChange}
            className="w-full"
          />
          <MultiParentsWithMultiChildrenDropdownField
            allParents={selectableDivisions}
            parentsValue={selectedDivisions}
            parentLabel="部署"
            parentRequired
            parentsOnChange={onDivisionChange}
            allChildren={optionSelectableSections}
            childrenValue={optionSelectedSections}
            childLabel="課"
            childrenOnChange={onSectionChange}
            childRequired
          />
        </FilterWrapper>

        {/* データ表示部分 */}
        {hasData && (
          <div className="mt-8 space-y-6">
            <RichHeader
              title={progressData.survey.title}
              description={progressData.survey.description}
              icon="ioDocumentTextOutline"
            />

            {renderAveragePointsChart()}

            <ProgressTableWithQuestions
              columns={columns}
              questions={progressData.questions}
              renderCell={renderCell}
            />
          </div>
        )}

        {/* データが無い場合の表示 */}
        {hasNoData && (
          <NoDataDisplay message="期間と部署・課を選択してデータを表示してください" />
        )}
      </EmployeeLayoutMainContent>
      <EmployeeLayoutRightSidebar>
        <RightSidebarTitle title="CSV" />
        <div className="px-4 py-3 space-y-6">
          <TitleWithChild title="CSVをエクスポートする">
            <Button
              text="CSVエクスポート"
              color={"gray"}
              outline
              onClick={handleCSVExport}
              className="w-full"
            />
          </TitleWithChild>
        </div>
      </EmployeeLayoutRightSidebar>
    </EmployeeLayout>
  );
};
